import { memo, useState } from 'react';
import { withRole } from '../Common/withRole';
import { EditIconButton, PrimaryButton, DefaultButton } from '../Common/BaseButton';
import { CalendarCog, CalendarDays } from 'lucide-react';
import { updateEventDateTime, makeGoogleApiRequest } from '../../services/supabase';
import { formatDateForDisplay, formatTimeForDisplay } from '../../utils/utils';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { supabase } from '../../services/supabase';
import { formatDateRangeForDisplay, formatTimeRangeForDisplay } from '../../utils/utils';

const MapLink = styled.a`
  color: var(--primary-green);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  font-size: 1.1rem;
  gap: 0.3rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DateTimeDisplay = styled.span`
  display: inline-flex;
  align-items: center;
  color: #333;
`;

const EditingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  max-width: fit-content;

`;

const InputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 1rem;
  margin-top: 0.5rem;
`;

const ReadOnlyDateTime = memo(({ event, assignments, user }) => {

  const hasGoogleEventAccess = event.google_event_link && 
    assignments && 
    Array.isArray(assignments) && 
    assignments.some(a => a.user?.id === user?.id);

  if (hasGoogleEventAccess) {
    return (
      <MapLink
        href={event.google_event_link}
        target="_blank"
        rel="noopener noreferrer"
      >
        <CalendarDays size={16} style={{ color: 'var(--primary-green)' }} /> 
        {formatDateRangeForDisplay(event.start_time, event.end_time, event.timezone)} • {formatTimeRangeForDisplay(event.start_time, event.end_time, event.timezone)}
      </MapLink>
    );
  }

  return (
    <DateTimeDisplay>
      <CalendarDays size={16} style={{ color: 'var(--primary-green)', marginRight: '0.3rem' }} /> 
      {formatDateRangeForDisplay(event.start_time, event.end_time, event.timezone)} • {formatTimeRangeForDisplay(event.start_time, event.end_time, event.timezone)}
    </DateTimeDisplay>
  );
});

const EditableDateTime = memo(({ event, onSuccess, isManager, isAuthenticated, assignments, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [startTime, setStartTime] = useState(event.start_time);
  const [endTime, setEndTime] = useState(event.end_time);
  const [eventDate, setEventDate] = useState(event.event_date);
  const [endDate, setEndDate] = useState(() => {
    // Calculate end date from end_time
    const endDateTime = DateTime.fromISO(event.end_time).setZone(event.timezone);
    return endDateTime.toFormat('yyyy-MM-dd');
  });
  
  // Store original multi-day state and dates for restoration
  const [originalEndDate] = useState(() => {
    const endDateTime = DateTime.fromISO(event.end_time).setZone(event.timezone);
    return endDateTime.toFormat('yyyy-MM-dd');
  });
  const [originalEndTime] = useState(event.end_time);
  const [originalIsMultiDay] = useState(() => {
    const startDateTime = DateTime.fromISO(event.start_time).setZone(event.timezone);
    const endDateTime = DateTime.fromISO(event.end_time).setZone(event.timezone);
    return !startDateTime.hasSame(endDateTime, 'day');
  });
  
  const [isMultiDay, setIsMultiDay] = useState(originalIsMultiDay);
  const [isSaving, setIsSaving] = useState(false);

  // Helper function to convert to local time
  const convertToLocalTime = (utcTime) => {
    if (!utcTime) return '';
    const dt = DateTime.fromISO(utcTime);
    if (!dt.isValid) return '';
    return dt.setZone(event.timezone).toFormat('HH:mm');
  };

  // Helper function to convert local time back to UTC
  const convertToUTC = (localDate, localTime) => {
    if (!localDate || !localTime) return null;
    const dt = DateTime.fromFormat(`${localDate}T${localTime}`, 'yyyy-MM-ddTHH:mm', { zone: event.timezone });
    if (!dt.isValid) return null;
    return dt.toUTC().toISO();
  };

  const handleDateChange = (newDate, isEndDate = false) => {
    if (!newDate) return;
    
    if (isEndDate) {
      setEndDate(newDate);
      // Update end time to use the new end date while preserving time
      const endDt = DateTime.fromISO(endTime).setZone(event.timezone);
      const newEndDt = DateTime.fromISO(newDate)
        .setZone(event.timezone)
        .set({ hour: endDt.hour, minute: endDt.minute });
      setEndTime(newEndDt.toUTC().toISO());
    } else {
      // Convert current times to the new date while preserving the time
      const startDt = DateTime.fromISO(startTime).setZone(event.timezone);
      const endDt = DateTime.fromISO(endTime).setZone(event.timezone);
      
      const newStartDt = DateTime.fromISO(newDate)
        .setZone(event.timezone)
        .set({ hour: startDt.hour, minute: startDt.minute });
      
      setEventDate(newDate);
      setStartTime(newStartDt.toUTC().toISO());
      
      // For single-day events, update end time to same date
      if (!isMultiDay) {
        const newEndDt = DateTime.fromISO(newDate)
          .setZone(event.timezone)
          .set({ hour: endDt.hour, minute: endDt.minute });
        setEndTime(newEndDt.toUTC().toISO());
        setEndDate(newDate);
      }
    }
  };

  const handleTimeInput = (timeValue, isStartTime) => {
    if (!event.timezone) return;
    
    // Use appropriate date for time calculation
    const targetDate = isStartTime ? eventDate : (isMultiDay ? endDate : eventDate);
    const dateStr = targetDate;
    const [hours, minutes] = timeValue.split(':').map(Number);
    const timeInEventTZ = DateTime.fromObject({
      year: parseInt(dateStr.split('-')[0]),
      month: parseInt(dateStr.split('-')[1]),
      day: parseInt(dateStr.split('-')[2]),
      hour: hours,
      minute: minutes
    }, { zone: event.timezone });
    
    if (isStartTime) {
      setStartTime(timeInEventTZ.toUTC().toISO());
    } else {
      setEndTime(timeInEventTZ.toUTC().toISO());
    }
  };

  const handleMultiDayToggle = (enabled) => {
    setIsMultiDay(enabled);
    
    if (!enabled) {
      // Single day mode - set end date to same as start date
      setEndDate(eventDate);
      const startDt = DateTime.fromISO(startTime).setZone(event.timezone);
      const endTimeOnSameDay = DateTime.fromISO(eventDate)
        .setZone(event.timezone)
        .set({ hour: startDt.hour + 2, minute: startDt.minute });
      setEndTime(endTimeOnSameDay.toUTC().toISO());
    } else {
      // Multi-day mode - restore original state if this was originally a multi-day event
      if (originalIsMultiDay) {
        setEndDate(originalEndDate);
        setEndTime(originalEndTime);
      } else {
        // If it was originally single-day, create new multi-day defaults
        const nextDay = DateTime.fromISO(eventDate).plus({ days: 1 }).toFormat('yyyy-MM-dd');
        setEndDate(nextDay);
        const endTimeNextDay = DateTime.fromISO(nextDay)
          .setZone(event.timezone)
          .set({ hour: 14, minute: 0 });
        setEndTime(endTimeNextDay.toUTC().toISO());
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Validate times in the event's timezone
      const startDateTime = DateTime.fromISO(startTime).setZone(event.timezone);
      const endDateTime = DateTime.fromISO(endTime).setZone(event.timezone);
      
      if (startDateTime >= endDateTime) {
        alert('End time must be after start time');
        return;
      }
      
      // Remove same-day validation for multi-day events
      if (!isMultiDay && !startDateTime.hasSame(endDateTime, 'day')) {
        alert('Start and end times must be on the same day for single-day events');
        return;
      }

      // Convert times to UTC before saving
      const utcStartTime = startDateTime.toUTC().toISO();
      const utcEndTime = endDateTime.toUTC().toISO();

      const { data, error } = await updateEventDateTime(event.id, {
        startTime: utcStartTime,
        endTime: utcEndTime,
        eventDate
      });

      if (error) throw error;

      // Update Google Calendar event if it exists
      if (event.google_event_id) {
        try {
          const googleEvent = {
            start: {
              dateTime: startDateTime.toISO(),
              timeZone: event.timezone,
            },
            end: {
              dateTime: endDateTime.toISO(),
              timeZone: event.timezone,
            }
          };

          const response = await makeGoogleApiRequest(
            `https://www.googleapis.com/calendar/v3/calendars/primary/events/${event.google_event_id}`,
            {
              method: 'PATCH',
              body: JSON.stringify(googleEvent),
            }
          );

          if (!response.ok) {
            throw new Error('Failed to update Google Calendar event');
          }
        } catch (error) {
          console.error('Error updating Google Calendar event:', error);
        }
      }
      
      // Call onSuccess with the updated event data
      if (onSuccess) {
        onSuccess({
          ...event,
          start_time: utcStartTime,
          end_time: utcEndTime,
          event_date: eventDate
        });
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update date/time:', err);
      alert('Failed to update date/time. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setStartTime(event.start_time);
    setEndTime(event.end_time);
    setEventDate(event.event_date);
    setEndDate(originalEndDate);
    setIsMultiDay(originalIsMultiDay);
  };

  if (isEditing) {
    return (
      <>
        <ReadOnlyDateTime event={event} assignments={assignments} user={user} />
        <EditingContainer>
          {/* Multi-day toggle */}
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={isMultiDay}
                onChange={(e) => handleMultiDayToggle(e.target.checked)}
              />
              Multi-day event
            </label>
          </div>
          
          <InputGroup>
            <input
              type="date"
              value={DateTime.fromISO(eventDate).toFormat('yyyy-MM-dd')}
              onChange={(e) => handleDateChange(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            {isMultiDay && (
              <>
                <span>to</span>
                <input
                  type="date"
                  value={DateTime.fromISO(endDate).toFormat('yyyy-MM-dd')}
                  onChange={(e) => handleDateChange(e.target.value, true)}
                  min={DateTime.fromISO(eventDate).toFormat('yyyy-MM-dd')}
                  style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </>
            )}
          </InputGroup>
          
          <InputGroup>
            <input
              type="time"
              value={convertToLocalTime(startTime)}
              onChange={(e) => handleTimeInput(e.target.value, true)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            <span>to</span>
            <input
              type="time"
              value={convertToLocalTime(endTime)}
              onChange={(e) => handleTimeInput(e.target.value, false)}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
            {isMultiDay && (
              <small style={{ color: '#666', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                (end time on {DateTime.fromISO(endDate).toFormat('MMM dd')})
              </small>
            )}
          </InputGroup>
          
          <ButtonGroup>
            <DefaultButton onClick={handleCancel}>
              Cancel
            </DefaultButton>
            <PrimaryButton 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </PrimaryButton>
          </ButtonGroup>
        </EditingContainer>
      </>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <ReadOnlyDateTime event={event} assignments={assignments} user={user} />
      <EditIconButton onClick={handleEdit}>
        <CalendarCog size={18} />
      </EditIconButton>
    </div>
  );
});

const WithRoleDateTime = withRole(EditableDateTime, {
  managerOnly: true,
  fallback: ReadOnlyDateTime
});

const EditEventDateTimeButton = memo(({ event, onSuccess, isManager, isAuthenticated, assignments, user }) => {
  return (
    <WithRoleDateTime
      event={event}
      onSuccess={onSuccess}
      isManager={isManager}
      isAuthenticated={isAuthenticated}
      assignments={assignments}
      user={user}
    />
  );
});

export default EditEventDateTimeButton;